import React from 'react'
import { ComponentConfig } from '@puckeditor/core'
import { HeroSection } from 'components/HeroSection/HeroSection'
import { getPuckFileUrl, UploadedFile } from '../../fields/FileUploadField'
import type { ThemeCustomFields } from '../../theme-config'
import { withLayout } from '../Layout'

export type HeroSectionProps = {
  eyebrow?: string
  title?: string
  subtitle?: string
  cta?: string
  ctaUrl?: string
  variant?: 'stacked' | 'column'
  contentPosition?: 'left' | 'right' | 'center'
  imagePosition?: 'left' | 'right'
  imageJustify?: 'left' | 'center' | 'right'
  backgroundColor?: string
  backgroundImage?: UploadedFile | string
  maxSectionHeight?: string
  minSectionHeight?: string
  target?: '_self' | '_blank'
}

// withLayout adds padding/margin/size on a wrapper (BR-5406);
// disableStyleFields because the hero manages its own background fields.
export const PuckHeroSection = withLayout<ComponentConfig<{
  props: HeroSectionProps
  fields: ThemeCustomFields
}>>({
  label: 'Hero Section',
  // What the editor's agent reads about this block (AgentComponentNotes).
  ai: {
    description:
      'Large intro: eyebrow, title, subtitle and a button, over (stacked) or beside (column) an image.',
    fields: {
      variant:
        'stacked puts backgroundImage behind the text; column puts it beside the text (imagePosition).',
      cta: 'Button label; the button only shows when this has text.',
    },
  },
  defaultProps: {
    eyebrow: '',
    title: '',
    subtitle: '',
    cta: '',
    ctaUrl: '',
    variant: 'stacked',
    contentPosition: 'left',
    imagePosition: 'left',
    imageJustify: 'left',
    backgroundColor: '',
    backgroundImage: '',
    maxSectionHeight: '',
    minSectionHeight: '',
    target: '_self',
  },
  fields: {
    eyebrow: { type: 'text', label: 'Eyebrow' },
    title: { type: 'text', label: 'Title' },
    subtitle: { type: 'textarea', label: 'Subtitle' },
    cta: { type: 'text', label: 'CTA Label' },
    ctaUrl: { type: 'text', label: 'CTA URL' },
    target: {
      type: 'select',
      label: 'CTA Behavior',
      options: [
        { label: 'Same tab', value: '_self' },
        { label: 'New tab', value: '_blank' },
      ],
    },
    variant: {
      type: 'select',
      label: 'Variant',
      options: [
        { label: 'Stacked', value: 'stacked' },
        { label: 'Column', value: 'column' },
      ],
    },
    contentPosition: {
      type: 'select',
      label: 'Content Position',
      options: [
        { label: 'Left', value: 'left' },
        { label: 'Center', value: 'center' },
        { label: 'Right', value: 'right' },
      ],
    },
    imagePosition: {
      type: 'select',
      label: 'Image Position (column variant)',
      options: [
        { label: 'Left', value: 'left' },
        { label: 'Right', value: 'right' },
      ],
    },
    imageJustify: {
      type: 'select',
      label: 'Image Justify',
      options: [
        { label: 'Left', value: 'left' },
        { label: 'Center', value: 'center' },
        { label: 'Right', value: 'right' },
      ],
    },
    backgroundColor: { type: 'colorPicker', label: 'Background Color' },
    backgroundImage: { type: 'fileUpload', label: 'Background Image' },
    maxSectionHeight: {
      type: 'text',
      label: 'Max Section Height (e.g. 500px)',
    },
    minSectionHeight: {
      type: 'text',
      label: 'Min Section Height (e.g. 500px)',
    },
  },
  render: ({ backgroundImage, ...rest }) => {
    const imageUrl =
      typeof backgroundImage === 'string'
        ? backgroundImage
        : getPuckFileUrl(backgroundImage)

    return <HeroSection {...rest} imageUrl={imageUrl} />
  },
}, { disableStyleFields: true })
